import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

// Résoudre le problème d'icône Leaflet dans React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const ProviderMap = ({ providers, onSelectProvider, clusters, mapType = 'standard' }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersLayer = useRef(null);
  const clustersLayer = useRef(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // Créer la carte au montage du composant
  useEffect(() => {
    if (!mapInstance.current && mapRef.current) {
      // Initialiser la carte avec un zoom et une position par défaut
      const map = L.map(mapRef.current, {
        center: [0, 0],  // Will be updated based on providers
        zoom: 2,
        minZoom: 2,
        maxZoom: 18,
        zoomControl: true,
      });

      // Ajouter un fond de carte OpenStreetMap
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      // Ajouter des contrôles de zoom en haut à droite
      map.zoomControl.setPosition('topright');

      // Créer une couche pour les marqueurs individuels
      markersLayer.current = L.layerGroup().addTo(map);

      // Créer une couche pour les clusters
      clustersLayer.current = L.markerClusterGroup({
        showCoverageOnHover: true,
        zoomToBoundsOnClick: true,
        spiderfyOnMaxZoom: true,
        disableClusteringAtZoom: 16,
        maxClusterRadius: 50,
        iconCreateFunction: function(cluster) {
          // Compter les prestataires en ligne et hors ligne dans ce cluster
          let online = 0;
          let offline = 0;
          
          cluster.getAllChildMarkers().forEach(marker => {
            if (marker.options.provider.isOnline) {
              online++;
            } else {
              offline++;
            }
          });
          
          const total = online + offline;
          
          // Déterminer la couleur du cluster en fonction du ratio en ligne/hors ligne
          let className = 'marker-cluster-';
          let backgroundColor = '';
          
          if (online === total) {
            className += 'success';
            backgroundColor = 'rgba(43, 193, 85, 0.6)';
          } else if (offline === total) {
            className += 'danger';
            backgroundColor = 'rgba(249, 70, 135, 0.6)';
          } else {
            className += 'mixed';
            backgroundColor = 'rgba(70, 130, 180, 0.6)';
          }
          
          // Créer le HTML pour l'icône de cluster
          return L.divIcon({
            html: `<div style="background-color: ${backgroundColor}">
                    <span>${total}</span>
                    <div class="cluster-details">
                      <span class="online">${online}</span>
                      <span class="offline">${offline}</span>
                    </div>
                  </div>`,
            className: className,
            iconSize: new L.Point(40, 40)
          });
        }
      });
      
      map.addLayer(clustersLayer.current);

      // Sauvegarder l'instance de la carte
      mapInstance.current = map;
    }

    // Nettoyer la carte à la suppression du composant
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  // Mettre à jour les marqueurs lorsque les prestataires changent
  useEffect(() => {
    if (!mapInstance.current || !providers || providers.length === 0) return;

    // Effacer les couches de marqueurs existantes
    markersLayer.current.clearLayers();
    clustersLayer.current.clearLayers();

    // Collecter les coordonnées valides pour centrer la carte
    const validCoordinates = [];

    // Créer les marqueurs pour chaque prestataire
    providers.forEach(provider => {
      if (provider.lat && provider.lon) {
        const position = [provider.lat, provider.lon];
        validCoordinates.push(position);
        
        // Créer une icône personnalisée en fonction du statut en ligne/hors ligne
        const customIcon = L.divIcon({
          className: `custom-div-icon ${provider.isOnline ? 'online' : 'offline'}`,
          html: `<div class="marker-pin ${provider.isOnline ? 'bg-success' : 'bg-danger'}">
                  <span>${provider.providerName.charAt(0)}</span>
                </div>`,
          iconSize: [30, 42],
          iconAnchor: [15, 42],
          popupAnchor: [0, -40]
        });

        // Créer le marqueur et l'ajouter à la couche appropriée
        const marker = L.marker(position, {
          icon: customIcon,
          provider: provider, // Stocker l'objet prestataire pour une utilisation ultérieure
          title: provider.providerName
        });

        // Ajouter un popup
        marker.bindTooltip(`
          <div class="provider-tooltip">
            <h6>${provider.providerName}</h6>
            <div class="provider-tooltip-type">${provider.providerTypeName}</div>
            <div class="provider-tooltip-city">${provider.city}</div>
            <div class="provider-tooltip-status ${provider.isOnline ? 'text-success' : 'text-danger'}">
              <i class="fa fa-${provider.isOnline ? 'wifi' : 'power-off'}"></i> 
              ${provider.isOnline ? 'En ligne' : 'Hors ligne'}
            </div>
          </div>
        `, { offset: [0, -35] });

        // Ajouter un événement de clic sur le marqueur
        marker.on('click', function() {
          onSelectProvider(provider);
        });

        // Ajouter le marqueur à la couche appropriée en fonction du type de carte
        if (mapType === 'clusters') {
          clustersLayer.current.addLayer(marker);
        } else {
          markersLayer.current.addLayer(marker);
        }
      }
    });

    // Ajuster la vue de la carte uniquement si c'est la première fois
    if (validCoordinates.length > 0 && initialLoad) {
      const bounds = L.latLngBounds(validCoordinates);
      mapInstance.current.fitBounds(bounds, { padding: [50, 50] });
      setInitialLoad(false);
    }

  }, [providers, mapType, onSelectProvider, initialLoad]);

  // Appliquer des styles CSS personnalisés pour les marqueurs
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-div-icon.online .marker-pin {
        background-color: #2bc155;
        border: 2px solid white;
      }
      
      .custom-div-icon.offline .marker-pin {
        background-color: #f94687;
        border: 2px solid white;
      }
      
      .marker-pin {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 3px 5px rgba(0,0,0,0.2);
      }
      
      .marker-pin span {
        color: white;
        font-weight: bold;
        font-size: 14px;
      }
      
      .provider-tooltip {
        text-align: center;
        min-width: 150px;
      }
      
      .provider-tooltip h6 {
        margin-bottom: 0.25rem;
        font-weight: 600;
      }
      
      .provider-tooltip-type,
      .provider-tooltip-city {
        font-size: 0.8rem;
        color: #666;
      }
      
      .provider-tooltip-status {
        margin-top: 0.5rem;
        font-weight: 600;
        font-size: 0.85rem;
      }
      
      .marker-cluster div {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
      }
      
      .marker-cluster-success div {
        background-color: rgba(43, 193, 85, 0.8);
      }
      
      .marker-cluster-danger div {
        background-color: rgba(249, 70, 135, 0.8);
      }
      
      .marker-cluster-mixed div {
        background-color: rgba(70, 130, 180, 0.8);
      }
      
      .cluster-details {
        font-size: 0.75rem;
        display: flex;
        gap: 5px;
      }
      
      .cluster-details .online {
        color: #d4ffde;
      }
      
      .cluster-details .offline {
        color: #ffd0e2;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div 
      ref={mapRef} 
      style={{ width: '100%', height: '100%' }}
      className="leaflet-provider-map"
    />
  );
};

export default ProviderMap;